import React, { useState, useEffect, useRef } from 'react';
import api from '../api/client';
import './PopupWMProduction.css';

const PopupWMProduction = ({ isOpen, onClose, mode, initialData }) => {
  const [recID, setRecID] = useState('');
  const [GTIN, setGTIN] = useState('');
  const [productName, setProductName] = useState('');
  const [packsPerBox, setPacksPerBox] = useState(null);
  const [boxSize, setBoxSize] = useState(null);
  const [seedsLbPerPack, setSeedsLbPerPack] = useState(null);
  const [, setSeedsIDForProduct] = useState(null);
  const [SKUForProduct, setSKUForProduct] = useState('');
  const [prodDate, setProdDate] = useState('');
  const [employee, setEmployee] = useState('');
  const [employees, setEmployees] = useState([]);

  // Extra employees
  const [extraEmployees, setExtraEmployees] = useState([]);
  const [extraEmployeeSelect, setExtraEmployeeSelect] = useState('');
  const [showExtraEmployees, setShowExtraEmployees] = useState(false);

  // Tracks whether the prodwm record exists in DB already
  const [isRecordCreated, setIsRecordCreated] = useState(false);

  // Boxes section
  const [boxes, setBoxes] = useState([]);
  const [scanInput, setScanInput] = useState('');
  const [editingBoxIndex, setEditingBoxIndex] = useState(null);
  const [editBoxValues, setEditBoxValues] = useState({});

  // Bags section
  const [bags, setBags] = useState([]);
  const [bagScanInput, setBagScanInput] = useState('');
  const [bagError, setBagError] = useState('');
  const [editingBagIndex, setEditingBagIndex] = useState(null);
  const [editBagValues, setEditBagValues] = useState({});
  const [returnModal, setReturnModal] = useState(null); // { index, bagnum, weight }

  const [containers, setContainers] = useState([]);

  const [completeProduction, setCompleteProduction] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [gtinError, setGtinError] = useState('');
  const [boxDuplicateWarning, setBoxDuplicateWarning] = useState('');
  const [saving, setSaving] = useState(false);

  const bagScanRef = useRef(null);
  const recIDRef = useRef('');
  const SKURef = useRef('');

  useEffect(() => { recIDRef.current = recID; }, [recID]);
  useEffect(() => { SKURef.current = SKUForProduct; }, [SKUForProduct]);

  function formatDateOnly(value) {
    if (!value) return '';
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    return d.toISOString().split('T')[0];
  }

  // Formats a datetime string (YYYY-MM-DD HH:mm:ss) for display as YYYY-MM-DD HH:mm
  function formatBoxDatetime(value) {
    if (!value) return '';
    // Already a clean string like "2026-03-19 15:46:08" — just trim seconds
    const s = String(value).replace('T', ' ').replace(/\.\d+Z?$/, '');
    return s.length >= 16 ? s.slice(0, 16) : s;
  }

  useEffect(() => {
    if (!isOpen) return;

    const fetchEmployees = async () => {
      try {
        const r = await api.get(`/api/employees-wm`);
        setEmployees(r.data);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };

    const fetchContainers = async () => {
      try {
        const r = await api.get(`/api/containers`);
        setContainers(r.data);
      } catch (err) {
        console.error('Error fetching containers:', err);
      }
    };
    fetchContainers();

    if (mode === 'edit' && !initialData) return;

    if (mode === 'add') {
      const fetchNextRecID = async () => {
        try {
          const r = await api.get(`/api/next-rec-id`);
          setRecID(r.data.nextRecId);
        } catch (err) {
          console.error('Error fetching next recID:', err);
        }
      };
      fetchNextRecID();
      fetchEmployees();
      setGTIN('');
      setProductName('');
      setPacksPerBox(null);
      setBoxSize(null);
      setSeedsIDForProduct(null);
      setSKUForProduct('');
      setProdDate('');
      setEmployee('');
      setExtraEmployees([]);
      setExtraEmployeeSelect('');
      setShowExtraEmployees(false);
      setBoxes([]);
      setScanInput('');
      setBags([]);
      setBagScanInput('');
      setCompleteProduction(false);
      setErrorMessage('');
      setGtinError('');
      setBoxDuplicateWarning('');
      setEditingBagIndex(null);
      setEditingBoxIndex(null);
      setIsRecordCreated(false);
    } else if (mode === 'edit' && initialData) {
      fetchEmployees();
      setRecID(initialData.recID || '');
      setIsRecordCreated(true);

      const gtinVal = initialData.GTIN || '';
      setGTIN(gtinVal);

      if (gtinVal) {
        api.get(`/api/wildmicr/gtin/${gtinVal}`)
          .then(r => {
            setProductName(r.data.packName || '');
            setPacksPerBox(r.data.bagsPerBox ?? null);
            setBoxSize(r.data.box_size || null);
            setSeedsLbPerPack(r.data.seedslbperpack ?? null);
            setSeedsIDForProduct(r.data.seedsID ?? null);
            setSKUForProduct(r.data.SKU || '');
          })
          .catch(err => console.error('Error fetching product for edit:', err));
      }

      const log = initialData.productionLog || [];
      if (log.length > 0) {
        const firstDay = log[0];
        setProdDate(firstDay.date ? formatDateOnly(firstDay.date) : '');
        setEmployee(String(firstDay.emplID || firstDay.employee || ''));
      }

      const allBoxes = (initialData.allBoxes || []).map(box => ({
        boxNumber: box.boxnum || '',
        qty: parseInt(box.qty_per_box, 10) || 0,
        date_added: formatBoxDatetime(box.date_added),
      }));
      setBoxes(allBoxes);

      const loadedBags = (initialData.bagsTaken || []).map(bag => ({
        seedsName: bag.seedsName || '',
        bagnum: bag.bagnum || bag.bagnumID || '',
        seedsID: String(bag.seedsid || bag.seedsID || ''),
        containerID: bag.containerID || '',
        containerName: bag.containerName || '',
        takenWeight: parseFloat(bag.takenWeight) || 0,
        returnedWeight: parseFloat(bag.returnedWeight) || 0,
        lot: bag.lot || '',
      }));
      setBags(loadedBags);

      const loadedExtra = initialData.extraEmployees || [];
      setExtraEmployees(loadedExtra);
      setExtraEmployeeSelect('');
      setShowExtraEmployees(loadedExtra.length > 0);

      setCompleteProduction(!!initialData.completeProduction);
      setErrorMessage('');
      setGtinError('');
      setBoxDuplicateWarning('');
      setEditingBagIndex(null);
      setEditingBoxIndex(null);
    }
  }, [mode, initialData, isOpen]);

  const fetchProductByGTIN = async (gtinVal) => {
    if (!gtinVal) return;
    setGtinError('');
    setProductName('');
    try {
      const r = await api.get(`/api/wildmicr/gtin/${gtinVal}`);
      setProductName(r.data.packName || '');
      setPacksPerBox(r.data.bagsPerBox ?? null);
      setBoxSize(r.data.box_size || null);
      setSeedsLbPerPack(r.data.seedslbperpack ?? null);
      setSeedsIDForProduct(r.data.seedsID ?? null);
      setSKUForProduct(r.data.SKU || '');
    } catch (err) {
      setGtinError('Product not found for this GTIN');
      setProductName('');
      setPacksPerBox(null);
      setBoxSize(null);
      setSeedsLbPerPack(null);
      setSKUForProduct('');
    }
  };

  const ensureRecordExists = async () => {
    if (isRecordCreated) return true;
    const currentRecID = recIDRef.current;
    const currentSKU = SKURef.current;
    if (!currentSKU) { setErrorMessage('Please enter a valid GTIN first'); return false; }
    if (!prodDate) { setErrorMessage('Production date is required'); return false; }
    if (!employee) { setErrorMessage('Employee is required'); return false; }
    try {
      await api.post(`/api/prodwm-header`, {
        recID: currentRecID,
        SKU: currentSKU,
        emplID: employee,
        prodDate,
        completeProduction: completeProduction ? 1 : 0,
      });
      setIsRecordCreated(true);
      return true;
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to create production record');
      return false;
    }
  };

  // ── Extra employees ────────────────────────────────────────────────────────

  const handleAddExtraEmployee = async () => {
    if (!extraEmployeeSelect) return;
    if (extraEmployees.some(e => String(e.employeeID) === String(extraEmployeeSelect))) return;
    if (String(employee) === String(extraEmployeeSelect)) return;

    const ready = await ensureRecordExists();
    if (!ready) return;

    try {
      await api.post(`/api/prodwm-employee-add`, {
        recID: recIDRef.current,
        employeeID: extraEmployeeSelect,
      });
      const found = employees.find(e => String(e.employeeID) === String(extraEmployeeSelect));
      setExtraEmployees(prev => [...prev, {
        employeeID: extraEmployeeSelect,
        employeeName: found?.employeeName || '',
        addedtoproductiondate: new Date().toISOString(),
      }]);
      setExtraEmployeeSelect('');
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to add employee');
    }
  };

  const handleRemoveExtraEmployee = async (employeeID) => {
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prodwm-employee/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(employeeID)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to remove employee');
        return;
      }
    }
    setExtraEmployees(prev => prev.filter(e => String(e.employeeID) !== String(employeeID)));
  };

  // ── Boxes ──────────────────────────────────────────────────────────────────

  const handleAddBoxes = async () => {
    if (!scanInput.trim()) return;
    setErrorMessage('');
    setBoxDuplicateWarning('');
    let parsed;
    try {
      const lines = scanInput.split('\n').filter(line => line.trim());
      parsed = lines.map((line, i) => {
        const parts = line.split(',').map(p => p.trim());
        if (parts.length < 3) throw new Error(`Row ${i + 1}: Invalid format`);
        // Product names may contain commas ("Strawflower, Tall Mixed") —
        // SKU and BOX#/QTY are always the last two parts, name is everything before
        const scannedSKU = parts[parts.length - 2];
        if (SKURef.current && scannedSKU !== SKURef.current) {
          throw new Error(`Row ${i + 1}: Wrong product — scanned SKU "${scannedSKU}", expected "${SKURef.current}"`);
        }
        const [rawBoxNum, rawQty] = parts[parts.length - 1].split('/');
        if (!rawBoxNum || !rawQty) throw new Error(`Row ${i + 1}: Invalid BOX/QTY format`);
        return {
          boxNumber: rawBoxNum.replace('BOX#', '').trim(),
          qty: parseInt(rawQty.trim(), 10),
        };
      });
    } catch (err) {
      setErrorMessage(err.message);
      return;
    }

    const ready = await ensureRecordExists();
    if (!ready) return;

    const currentRecID = recIDRef.current;
    const currentSKU = SKURef.current;
    const addedBoxes = [];

    for (const box of parsed) {
      try {
        const { data: boxRes } = await api.post(`/api/prodwm-box-add`, {
          recID: currentRecID,
          boxnum: box.boxNumber,
          sku: currentSKU,
          qty_per_box: box.qty,
          emplID: employee,
        });
        addedBoxes.push({ ...box, date_added: boxRes.date_added || '' });
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.existingRecID) {
          setBoxDuplicateWarning(err.response.data.error);
        } else {
          setErrorMessage(err.response?.data?.error || `Failed to add box ${box.boxNumber}`);
        }
        break;
      }
    }

    if (addedBoxes.length > 0) {
      setBoxes(prev => [...prev, ...addedBoxes]);
      setScanInput('');
    }
  };

  const handleRemoveBox = async (index) => {
    const box = boxes[index];
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prodwm-box/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(box.boxNumber)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || `Failed to remove box ${box.boxNumber}`);
        return;
      }
    }
    if (editingBoxIndex === index) setEditingBoxIndex(null);
    setBoxes(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditBox = (index) => {
    setEditingBoxIndex(index);
    setEditBoxValues({ newBoxNumber: boxes[index].boxNumber, qty: boxes[index].qty });
  };

  const handleSaveBoxEdit = async (index) => {
    const box = boxes[index];
    const newBoxNumber = editBoxValues.newBoxNumber?.trim() || box.boxNumber;
    const newQty = parseInt(editBoxValues.qty, 10) || box.qty;

    if (isRecordCreated) {
      try {
        await api.put(`/api/prodwm-box`, {
          recID: recIDRef.current,
          oldBoxnum: box.boxNumber,
          newBoxnum: newBoxNumber,
          qty_per_box: newQty,
        });
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to update box');
        return;
      }
    }
    setBoxes(prev => prev.map((b, i) => i === index ? { ...b, boxNumber: newBoxNumber, qty: newQty } : b));
    setEditingBoxIndex(null);
    setEditBoxValues({});
  };

  const handleCancelBoxEdit = () => {
    setEditingBoxIndex(null);
    setEditBoxValues({});
  };

  // ── Bags ───────────────────────────────────────────────────────────────────

  const handleBagScanKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddBag();
    }
  };

  const handleAddBag = async () => {
    const bagnum = bagScanInput.trim().toUpperCase();
    if (!bagnum) return;
    if (!GTIN) { setBagError('Please enter GTIN first'); return; }
    if (bags.some(b => b.bagnum.toUpperCase() === bagnum)) {
      setBagError(`Bag ${bagnum} is already in the list`);
      return;
    }
    setBagError('');

    let bagData;
    try {
      const r = await api.get(`/api/prodwm-bag-lookup?bagnum=${encodeURIComponent(bagnum)}&gtin=${encodeURIComponent(GTIN)}`);
      bagData = r.data;
    } catch (err) {
      setBagError(err.response?.data?.error || 'Error looking up bag');
      return;
    }

    const ready = await ensureRecordExists();
    if (!ready) return;

    const currentRecID = recIDRef.current;
    try {
      const r = await api.post(`/api/prodwm-bag-add`, {
        recID: currentRecID,
        bagnum: bagData.bagnum || bagnum,
        seedsid: bagData.seedsID,
        containerID: bagData.containerID || null,
        lot: bagData.lot || null,
        emplID: employee,
      });
      setBags(prev => [...prev, {
        seedsName: bagData.seedsName || '',
        bagnum: bagData.bagnum || bagnum,
        seedsID: String(bagData.seedsID || ''),
        containerID: bagData.containerID || '',
        containerName: bagData.containerName || '',
        takenWeight: r.data.takenWeight ?? (parseFloat(bagData.currentWeight) || 0),
        returnedWeight: 0,
        lot: bagData.lot || '',
      }]);
      setBagScanInput('');
      bagScanRef.current?.focus();
    } catch (err) {
      setBagError(err.response?.data?.error || 'Failed to add bag');
    }
  };

  const handleRemoveBag = async (index) => {
    const bag = bags[index];
    if (isRecordCreated) {
      try {
        await api.delete(`/api/prodwm-bag/${encodeURIComponent(recIDRef.current)}/${encodeURIComponent(bag.bagnum)}`);
      } catch (err) {
        setErrorMessage(err.response?.data?.error || `Failed to remove bag ${bag.bagnum}`);
        return;
      }
    }
    if (editingBagIndex === index) setEditingBagIndex(null);
    setBags(prev => prev.filter((_, i) => i !== index));
  };

  const handleEditBag = (index) => {
    setEditingBagIndex(index);
    setEditBagValues({
      containerID: bags[index].containerID || '',
      containerName: bags[index].containerName || '',
      takenWeight: bags[index].takenWeight,
      returnedWeight: bags[index].returnedWeight,
      lot: bags[index].lot || '',
    });
  };

  const handleSaveBagEdit = async (index) => {
    const bag = bags[index];
    const payload = {
      recID: recIDRef.current,
      bagnum: bag.bagnum,
      seedsid: bag.seedsID,
      containerID: editBagValues.containerID || null,
      lot: editBagValues.lot ?? '',
      takenWeight: parseFloat(editBagValues.takenWeight) || 0,
      returnedWeight: parseFloat(editBagValues.returnedWeight) || 0,
      emplID: employee,
    };

    let netReturn = parseFloat(editBagValues.returnedWeight) || 0;

    if (isRecordCreated) {
      try {
        const r = await api.put(`/api/prodwm-bag-update`, payload);
        netReturn = r.data.netReturn ?? netReturn;
      } catch (err) {
        setErrorMessage(err.response?.data?.error || 'Failed to update bag');
        return;
      }
    }

    const found = containers.find(c => String(c.containerID) === String(editBagValues.containerID));
    setBags(prev => prev.map((b, i) => i === index ? {
      ...b,
      containerID: editBagValues.containerID,
      containerName: found?.containerName || editBagValues.containerName || '',
      takenWeight: parseFloat(editBagValues.takenWeight) || 0,
      returnedWeight: netReturn,
      lot: editBagValues.lot,
    } : b));
    setEditingBagIndex(null);
    setEditBagValues({});
  };

  const handleCancelBagEdit = () => {
    setEditingBagIndex(null);
    setEditBagValues({});
  };

  const handleReturnSubmit = async () => {
    const { index, weight } = returnModal;
    const bag = bags[index];
    const payload = {
      recID: recIDRef.current,
      bagnum: bag.bagnum,
      seedsid: bag.seedsID,
      containerID: bag.containerID || null,
      lot: bag.lot ?? '',
      returnedWeight: parseFloat(weight) || 0,
      emplID: employee,
    };
    try {
      const r = await api.put(`/api/prodwm-bag-update`, payload);
      const netReturn = r.data.netReturn ?? parseFloat(weight) ?? 0;
      setBags(prev => prev.map((b, i) => i === index ? { ...b, returnedWeight: netReturn } : b));
      setReturnModal(null);
    } catch (err) {
      setErrorMessage(err.response?.data?.error || 'Failed to save return weight');
    }
  };

  // ── Totals ─────────────────────────────────────────────────────────────────

  const totalBoxesCount = boxes.length;
  const totalBoxesQty = boxes.reduce((sum, b) => sum + (b.qty || 0), 0);
  const totalTakenLb = bags.reduce((sum, b) => sum + (parseFloat(b.takenWeight) || 0), 0);
  const requiredLb = seedsLbPerPack != null ? totalBoxesQty * seedsLbPerPack : null;
  const SEEDS_TOLERANCE_LB = 2; // agreed with Vlada 07.10.2026: up to 2 LB less than required is allowed (was 10%)
  const allowedMinLb = requiredLb != null ? requiredLb - SEEDS_TOLERANCE_LB : null;
  const deficitLb = allowedMinLb != null && boxes.length > 0 && totalTakenLb < allowedMinLb
    ? +(allowedMinLb - totalTakenLb).toFixed(2) // rounded: float noise (599.9999) must not block completion
    : 0;
  // Over-use: net seeds consumed (taken − returned) exceeds required + 5% —
  // usually means the return from the station to the warehouse was not registered
  const totalReturnedLb = bags.reduce((sum, b) => sum + (parseFloat(b.returnedWeight) || 0), 0);
  const usedLb = totalTakenLb - totalReturnedLb;
  const allowedMaxLb = requiredLb > 0 ? requiredLb * 1.05 : null;
  const overuseLb = allowedMaxLb != null && boxes.length > 0 && usedLb > allowedMaxLb
    ? usedLb - allowedMaxLb
    : 0;

  // ── Header save ────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!GTIN) { setErrorMessage('GTIN is required'); return; }
    if (!SKUForProduct) { setErrorMessage('Product not found for this GTIN'); return; }
    if (!prodDate) { setErrorMessage('Production date is required'); return; }
    if (!employee) { setErrorMessage('Employee is required'); return; }
    setErrorMessage('');
    setSaving(true);
    try {
      await api.post(`/api/prodwm-header`, {
        recID,
        SKU: SKUForProduct,
        emplID: employee,
        prodDate,
        completeProduction: completeProduction ? 1 : 0,
      });
      setIsRecordCreated(true);
      onClose();
    } catch (error) {
      const msg = error.response?.data?.error || 'Failed to save production record';
      setErrorMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  // Employees available for extra (exclude primary and already added)
  const availableForExtra = employees.filter(e =>
    String(e.employeeID) !== String(employee) &&
    !extraEmployees.some(ex => String(ex.employeeID) === String(e.employeeID))
  );

  if (!isOpen) return null;

  return (
    <div className="popup open">
      <div className="popup__body popup__body--wide">
        <div className="popup__content popup__content--wide">
          <button className="popup__close-button" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <use xlinkHref="#closeBtn"></use>
            </svg>
          </button>

          <h2 className="popup__title">Production Microgreens / Wildflowers</h2>

          {/* ── Header row ─────────────────────────────────── */}
          <div className="prod-header-row">
            <div className="prod-header-field">
              <label className="label">Record ID</label>
              <input type="text" className="form__input" value={recID} readOnly />
            </div>

            <div className="prod-header-field">
              <label className="label">GTIN</label>
              <input
                type="text"
                className="form__input"
                value={GTIN}
                onChange={e => { setGTIN(e.target.value); setProductName(''); setGtinError(''); }}
                onBlur={() => fetchProductByGTIN(GTIN)}
                onKeyDown={e => { if (e.key === 'Enter') fetchProductByGTIN(GTIN); }}
                placeholder="Scan or enter GTIN"
              />
              {gtinError && <span className="field-error">{gtinError}</span>}
              {productName && (
                <div className="gtin-product-info">
                  <span className="gtin-product-name">{productName}</span>
                  {packsPerBox != null && (
                    <span className="gtin-packs-per-box">{packsPerBox} packs / box</span>
                  )}
                  {boxSize && <span className="gtin-box-size" title="Box size, inches"><i className="fa-solid fa-box"></i>{boxSize}</span>}
                </div>
              )}
            </div>

            <div className="prod-header-field">
              <label className="label">Production Date</label>
              <input
                type="date"
                className="form__input"
                value={prodDate}
                onChange={e => setProdDate(e.target.value)}
              />
            </div>

            <div className="prod-header-field">
              <label className="label">Employee</label>
              <select
                className="slct"
                value={employee}
                onChange={e => setEmployee(e.target.value)}
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.employeeID} value={emp.employeeID}>{emp.fullName || emp.employeeName}</option>
                ))}
              </select>
              <button
                className="prod-extra-employees-toggle"
                onClick={() => setShowExtraEmployees(v => !v)}
              >
                {showExtraEmployees ? '− Hide additional employees' : '+ Add more employees'}
                {!showExtraEmployees && extraEmployees.length > 0 && (
                  <span className="prod-extra-employees-count">{extraEmployees.length}</span>
                )}
              </button>
            </div>
          </div>

          {/* ── Extra employees row ────────────────────────── */}
          {showExtraEmployees && <div className="prod-extra-employees-row">
            <span className="label prod-extra-employees-label">Additional employees:</span>
            <div className="prod-extra-employees-add">
              <select
                className="slct prod-extra-employees-select"
                value={extraEmployeeSelect}
                onChange={e => setExtraEmployeeSelect(e.target.value)}
              >
                <option value="">Select employee…</option>
                {availableForExtra.map(emp => (
                  <option key={emp.employeeID} value={emp.employeeID}>{emp.fullName || emp.employeeName}</option>
                ))}
              </select>
              <button className="btn btn__white_g prod-add-btn" onClick={handleAddExtraEmployee}>
                + Add
              </button>
            </div>
            {extraEmployees.length > 0 && (
              <div className="prod-extra-employees-list">
                {extraEmployees.map(emp => (
                  <span key={emp.employeeID} className="prod-extra-employee-tag">
                    {emp.employeeName || `#${emp.employeeID}`}
                    <button
                      className="prod-extra-employee-remove"
                      title="Remove"
                      onClick={() => handleRemoveExtraEmployee(emp.employeeID)}
                    >×</button>
                  </span>
                ))}
              </div>
            )}
          </div>}

          {/* ── Two-column production section ─────────────── */}
          <div className="prod-columns-row">

            {/* LEFT: Boxes produced */}
            <div className="prod-column">
              <div className="prod-column-header">
                <span className="prod-column-title">Boxes Produced</span>
              </div>
              <div className="prod-scan-area">
                <textarea
                  className="scannerform__textarea"
                  rows="4"
                  placeholder="Scan boxes here (one per line)"
                  value={scanInput}
                  onChange={e => setScanInput(e.target.value)}
                />
                <button className="btn btn__white_g prod-add-btn" onClick={handleAddBoxes}>
                  ADD
                </button>
              </div>

              {boxDuplicateWarning && (
                <div style={{ color: '#b45309', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '4px', padding: '8px 12px', marginTop: '8px', fontWeight: 'bold' }}>
                  ⚠ {boxDuplicateWarning}
                </div>
              )}

              {boxes.length > 0 && (
                <table className="prod-table">
                  <thead>
                    <tr>
                      <th className="col-num">#</th>
                      <th>Box Number</th>
                      <th>Date Added</th>
                      <th>Qty in Box</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {boxes.map((box, i) => (
                      <tr key={i}>
                        <td className="col-num">{i + 1}</td>
                        <td>
                          {editingBoxIndex === i ? (
                            <input
                              type="text"
                              className="form__input prod-table__inline-input"
                              value={editBoxValues.newBoxNumber}
                              onChange={e => setEditBoxValues(v => ({ ...v, newBoxNumber: e.target.value }))}
                            />
                          ) : box.boxNumber}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b', fontSize: '12px' }}>
                          {formatBoxDatetime(box.date_added) || '—'}
                        </td>
                        <td>
                          {editingBoxIndex === i ? (
                            <input
                              type="number"
                              className="form__input prod-table__inline-input"
                              value={editBoxValues.qty}
                              onChange={e => setEditBoxValues(v => ({ ...v, qty: e.target.value }))}
                              style={{ width: '70px !important' }}
                            />
                          ) : box.qty}
                        </td>
                        <td className="col-actions">
                          <div className="prod-table__actions">
                            {editingBoxIndex === i ? (
                              <>
                                <button
                                  className="prod-table__icon-btn prod-table__icon-btn--confirm"
                                  title="Save"
                                  onClick={() => handleSaveBoxEdit(i)}
                                >
                                  <i className="fa fa-check"></i>
                                </button>
                                <button
                                  className="prod-table__icon-btn"
                                  title="Cancel"
                                  onClick={handleCancelBoxEdit}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="prod-table__icon-btn"
                                  title="Edit"
                                  onClick={() => handleEditBox(i)}
                                >
                                  <i className="fa fa-pencil"></i>
                                </button>
                                <button
                                  className="prod-table__icon-btn prod-table__icon-btn--danger"
                                  title="Remove"
                                  onClick={() => handleRemoveBox(i)}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="prod-table__total-row">
                      <td colSpan="3">Total: {totalBoxesCount} box{totalBoxesCount !== 1 ? 'es' : ''}</td>
                      <td>{totalBoxesQty} pcs</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            {/* RIGHT: Bags used */}
            <div className="prod-column">
              <div className="prod-column-header">
                <span className="prod-column-title">Seeds Bags Used</span>
              </div>
              <div className="bag-scan-row">
                <input
                  ref={bagScanRef}
                  type="text"
                  className="form__input"
                  placeholder="Scan bag number and press Enter..."
                  value={bagScanInput}
                  onChange={e => { setBagScanInput(e.target.value); setBagError(''); }}
                  onKeyDown={handleBagScanKeyDown}
                />
                <button className="btn btn__white_g prod-add-btn" onClick={handleAddBag}>+ Add</button>
              </div>
              {bagError && <div className="field-error">{bagError}</div>}

              {bags.length > 0 && (
                <table className="prod-table">
                  <thead>
                    <tr>
                      <th>Bag #</th>
                      <th>Lot</th>
                      <th>Container</th>
                      <th>Taken, LB</th>
                      <th>Returned, LB</th>
                      <th className="col-actions"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bags.map((bag, i) => (
                      <tr key={i}>
                        <td>{bag.bagnum}</td>
                        <td>
                          {editingBagIndex === i ? (
                            <input
                              type="text"
                              className="form__input prod-table__inline-input"
                              value={editBagValues.lot}
                              onChange={e => setEditBagValues(v => ({ ...v, lot: e.target.value }))}
                            />
                          ) : (bag.lot || '—')}
                        </td>
                        <td>
                          {editingBagIndex === i ? (
                            <select
                              className="slct prod-table__inline-input"
                              value={editBagValues.containerID}
                              onChange={e => {
                                const id = e.target.value;
                                const found = containers.find(c => String(c.containerID) === id);
                                setEditBagValues(v => ({ ...v, containerID: id, containerName: found?.containerName || '' }));
                              }}
                            >
                              <option value="">—</option>
                              {containers.map(c => (
                                <option key={c.containerID} value={String(c.containerID)}>{c.containerName}</option>
                              ))}
                            </select>
                          ) : (bag.containerName || '—')}
                        </td>
                        <td>
                          {editingBagIndex === i ? (
                            <input
                              type="number"
                              step="0.01"
                              className="form__input prod-table__inline-input"
                              value={editBagValues.takenWeight}
                              onChange={e => setEditBagValues(v => ({ ...v, takenWeight: e.target.value }))}
                            />
                          ) : bag.takenWeight}
                        </td>
                        <td>
                          {editingBagIndex === i ? (
                            <input
                              type="number"
                              step="0.01"
                              className="form__input prod-table__inline-input"
                              value={editBagValues.returnedWeight}
                              onChange={e => setEditBagValues(v => ({ ...v, returnedWeight: e.target.value }))}
                            />
                          ) : bag.returnedWeight}
                        </td>
                        <td className="col-actions">
                          <div className="prod-table__actions">
                            {editingBagIndex === i ? (
                              <>
                                <button
                                  className="prod-table__icon-btn prod-table__icon-btn--confirm"
                                  title="Save"
                                  onClick={() => handleSaveBagEdit(i)}
                                >
                                  <i className="fa fa-check"></i>
                                </button>
                                <button
                                  className="prod-table__icon-btn"
                                  title="Cancel"
                                  onClick={handleCancelBagEdit}
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="prod-table__icon-btn prod-return-btn"
                                  title="Return weight"
                                  onClick={() => setReturnModal({ index: i, bagnum: bag.bagnum, weight: '' })}
                                >
                                  Return
                                </button>
                                <button
                                  className="prod-table__icon-btn"
                                  title="Edit"
                                  onClick={() => handleEditBag(i)}
                                >
                                  <i className="fa fa-pencil"></i>
                                </button>
                                <button
                                  className="prod-table__icon-btn prod-table__icon-btn--danger"
                                  title="Remove"
                                  onClick={() => handleRemoveBag(i)}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ── Seeds weight warning ──────────────────────── */}
          {requiredLb != null && boxes.length > 0 && deficitLb > 0 && (
            <div className="prod-weight-warning">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Seeds weight insufficient.&nbsp;
                <strong>Required: {requiredLb.toFixed(2)} LB</strong>&nbsp;·&nbsp;
                <strong>Entered: {totalTakenLb.toFixed(2)} LB</strong>&nbsp;·&nbsp;
                <strong className="prod-weight-warning__deficit">Missing: {(requiredLb - totalTakenLb).toFixed(2)} LB</strong>.&nbsp;
                A shortage of up to {SEEDS_TOLERANCE_LB} LB is allowed, so at least {allowedMinLb.toFixed(2)} LB must be entered to complete production.
              </span>
            </div>
          )}
          {overuseLb > 0 && (
            <div className="prod-weight-warning">
              <i className="fa fa-exclamation-triangle"></i>
              <span>
                Seeds usage too high. If seeds were returned from the station to the warehouse, register the return first.&nbsp;
                <strong>Required: {requiredLb.toFixed(2)} LB</strong>&nbsp;·&nbsp;
                <strong>Used (taken − returned): {usedLb.toFixed(2)} LB</strong>&nbsp;·&nbsp;
                <strong className="prod-weight-warning__deficit">Excess: {overuseLb.toFixed(2)} LB</strong>
              </span>
            </div>
          )}

          {/* ── Footer ────────────────────────────────────── */}
          <div className="prod-footer-row">
            <label className="checkbox">
              <input
                type="checkbox"
                className="checkbox__real"
                checked={completeProduction}
                disabled={deficitLb > 0 || overuseLb > 0}
                onChange={e => setCompleteProduction(e.target.checked)}
              />
              <span className="checkbox__fake"></span>
              <span className="checkbox__title">Complete Production</span>
            </label>
            <button className="btn btn__green" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          {errorMessage && <div className="popup__error-message">{errorMessage}</div>}
        </div>
      </div>

      {/* ── Return weight mini-popup ───────────────────── */}
      {returnModal && (
        <div className="return-modal-overlay" onClick={() => setReturnModal(null)}>
          <div className="return-modal" onClick={e => e.stopPropagation()}>
            <button className="return-modal__close" onClick={() => setReturnModal(null)} title="Close">×</button>
            <div className="return-modal__header">
              <div className="return-modal__icon"><i className="fa fa-refresh"></i></div>
              <div>
                <div className="return-modal__title">Return Seeds</div>
                <div className="return-modal__bag">Bag <span className="return-modal__bagnum">{returnModal.bagnum}</span></div>
              </div>
            </div>
            <div className="return-modal__divider" />
            <div className="return-modal__field">
              <label className="return-modal__label">Return weight, LB</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="form__input return-modal__input"
                placeholder="0.00"
                value={returnModal.weight}
                onChange={e => setReturnModal(m => ({ ...m, weight: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter') handleReturnSubmit(); }}
                autoFocus
              />
              <span className="return-modal__hint">Container weight will be subtracted automatically</span>
            </div>
            <div className="return-modal__actions">
              <button className="btn btn__white_g return-modal__cancel" onClick={() => setReturnModal(null)}>Cancel</button>
              <button className="btn btn__green return-modal__submit" onClick={handleReturnSubmit}>
                <i className="fa fa-check"></i> Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PopupWMProduction;
